"use client";

import React from "react";

import ScienceSectionIntro from "@/components/common/ScienceSectionIntro/ScienceSectionIntro";
import Picture from "@/components/ui-kit/Picture/Picture";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { cn } from "@/utils/general";

export type InnovationMember = {
  name: string;
  position?: string;
  image: {
    src: { mobile: string; desktop: string };
    alt: string;
  };
};

export type InnovationFact = {
  value: number;
  text: string;
  suffix?: string;
  prefix?: string;
};

export type InnovationProps = {
  id?: string;
  intro?: {
    tag?: string;
    headline?: string;
    text?: string;
  };
  members: InnovationMember[];
  facts: {
    facts: InnovationFact[];
  };
};

function Member({ name, position, image }: InnovationMember) {
  return (
    <li className="flex gap-4 items-center w-full bg-master-base-default p-4 md:flex-1 border border-nova-transparency-black-20">
      <div className="min-w-[48px] w-12 lg:w-16">
        <Picture
          src={image.src.mobile}
          sources={[{ media: "(min-width: 768px)", srcSet: image.src.desktop }]}
          alt={image.alt}
          className="w-full"
          loading="lazy"
        />
      </div>
      <div className="flex flex-col gap-2">
        <TextElementNova
          text={name}
          classes={{
            default: "Body-30-Dia-Strong",
            768: "Body-20-Dia-Strong",
            1024: "Body-30-Dia-Strong",
          }}
        />
        {position && <TextElementNova text={position} className="Caption-10-Dia-Uppercase" />}
      </div>
    </li>
  );
}

function FactCard({ value, text, suffix = "", prefix = "" }: InnovationFact) {
  return (
    <li
      className={cn(
        "bg-nova-base-white p-4 flex flex-col gap-6 border border-nova-transparency-black-20",
        "md:gap-0 xl:p-10"
      )}
    >
      <span className="font-grilliBook text-[32px] leading-[1.25] text-[#22202f] md:text-[48px]">
        {prefix}
        {value}
        {suffix}
      </span>
      <TextElementNova
        className="mt-auto"
        classes={{
          default: "Caption-10-Dia-Normal",
          768: "Caption-10-Dia-Normal",
          1024: "Body-20-Dia-Normal",
        }}
        text={text}
      />
    </li>
  );
}

export default function Innovation({ intro, id, members, facts }: InnovationProps) {
  return (
    <section
      data-section
      id={id}
      className="relative z-[1] py-8 md:py-12 xl:py-20 bg-nova-base-solid-10"
    >
      <div className="w-full max-w-[1440px] mx-auto md:px-8">
        {intro && <ScienceSectionIntro {...intro} headingWidth={0.6} lgPaddingBottom={40} />}

        <div className="px-6 md:px-0 flex flex-wrap">
          {members?.length > 0 && (
            /*
             * Gatsby Box width={[1, 1, 284, 405]} with breakpoints 480/768/1024/1280
             * → 284px from 768, 405px from 1024 (Next lg), not 1280 (xl)
             */
            <div className="w-full md:w-[284px] md:min-w-[284px] lg:w-[405px] lg:min-w-[405px] pb-10 md:pb-0 md:order-1">
              <ul className="flex flex-col gap-2 xl:h-full">
                {members.map((member, index) => (
                  <Member key={index} {...member} />
                ))}
              </ul>
            </div>
          )}
          {facts?.facts?.length > 0 && (
            <div className="w-full md:w-[calc(100%-284px)] lg:w-[calc(100%-405px)] md:pr-2 md:order-0">
              <ul className="grid grid-cols-2 gap-2 h-full">
                {facts.facts.map((fact, index) => (
                  <FactCard key={index} {...fact} />
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
